import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  GraduationCap,
  BookOpen,
  Users,
  Award,
  Target,
  TrendingUp,
  Brain,
  Lightbulb,
  CheckCircle2,
  Shield,
  Building2,
  Rocket,
  Star,
  Heart,
  Briefcase,
  ChevronRight
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';

export const HEITraining = () => {
  const navigate = useNavigate();

  const pageData = {
    title: 'Higher Education Institution Training Programs',
    description: 'Comprehensive training solutions for higher education institutions including Faculty Development Programs, NAAC Accreditation, OBE Implementation, and more.',
    meta_title: 'HEI Training Programs - Faculty Development, NAAC, OBE & More | ISDE Academy',
    meta_description: 'Training programs for higher education institutions including Faculty Development Program (FDP), NAAC Accreditation, Outcome-Based Education (OBE), ISO 21001, Research Methodology, and Digital Teaching.',
    meta_keywords: 'hei training, faculty development program, FDP, NAAC accreditation, OBE implementation, ISO 21001, research methodology, digital teaching, higher education training',
    canonicalUrl: 'https://isde.academy/training/hei-training'
  };

  const trainingPrograms = [
    {
      id: 'faculty-development',
      title: 'Faculty Development Program (FDP)',
      duration: '1-5 Days',
      icon: GraduationCap,
      color: 'text-indigo-600',
      borderColor: 'border-indigo-600',
      bgColor: 'bg-indigo-50',
      description: 'Industry-aligned Faculty Development Programs designed to improve pedagogy, curriculum delivery, and academic performance for teaching professionals across all disciplines.',
      isClickable: true,
      link: '/training/faculty-development-program',
      linkText: 'View Full Program',
      courses: [
        {
          name: 'Teaching Pedagogy and Methodologies',
          duration: '2-3 Days',
          level: 'All Faculty',
          description: 'Modern student-centric teaching approaches including active learning, PBL, and flipped classrooms.'
        },
        {
          name: 'Outcome-Based Education (OBE)',
          duration: '2 Days',
          level: 'All Faculty',
          description: 'Course outcomes, program outcomes, Bloom\'s taxonomy, and accreditation alignment.'
        },
        {
          name: 'Research and Publication Skills',
          duration: '2-3 Days',
          level: 'Faculty & Researchers',
          description: 'Research methodology, academic writing, journal selection, and publication ethics.'
        },
        {
          name: 'Digital and Technology-Enabled Teaching',
          duration: '2 Days',
          level: 'All Faculty',
          description: 'LMS platforms, online teaching tools, AI in education, and e-content development.'
        },
        {
          name: 'Leadership and Academic Administration',
          duration: '2 Days',
          level: 'HODs & Academic Leaders',
          description: 'Academic leadership, institutional planning, quality assurance, and change management.'
        }
      ],
      benefits: [
        'Enhanced teaching effectiveness and student engagement',
        'Meet accreditation requirements (NBA, NAAC, NIRF)',
        'Improved research capabilities and publications',
        'Better use of educational technology',
        'Stronger academic leadership and institutional growth'
      ]
    },
    {
      id: 'naac-accreditation',
      title: 'NAAC Accreditation Preparation',
      duration: '2-3 Days',
      icon: Award,
      color: 'text-emerald-600',
      borderColor: 'border-emerald-600',
      bgColor: 'bg-emerald-50',
      description: 'Comprehensive preparation for NAAC assessment and accreditation process.',
      isClickable: true,
      link: '/training/naac-accreditation-preparation',
      linkText: 'View Full Program',
      courses: [
        {
          name: 'Understanding NAAC Framework',
          duration: '1 Day',
          level: 'All Staff',
          description: 'Overview of NAAC criteria, metrics, and assessment process.'
        },
        {
          name: 'Self-Study Report (SSR) Preparation',
          duration: '2 Days',
          level: 'Coordinators & Leaders',
          description: 'Creating comprehensive SSR with proper documentation and evidence.'
        },
        {
          name: 'Quality Enhancement Initiatives',
          duration: '1 Day',
          level: 'All Faculty',
          description: 'Implementing continuous quality improvement processes.'
        }
      ],
      benefits: [
        'Better understanding of NAAC requirements',
        'Improved institutional quality indicators',
        'Enhanced documentation practices',
        'Higher accreditation grades'
      ]
    },
    {
      id: 'obe-implementation',
      title: 'OBE Implementation Training',
      duration: '2-3 Days',
      icon: Target,
      color: 'text-blue-600',
      borderColor: 'border-blue-600',
      bgColor: 'bg-blue-50',
      description: 'Complete training on implementing Outcome-Based Education framework for NBA and NAAC accreditation.',
      isClickable: true,
      link: '/training/outcome-based-education-in-india',
      linkText: 'View Full Program',
      courses: [
        {
          name: 'OBE Framework and Principles',
          duration: '1 Day',
          level: 'All Faculty',
          description: 'Understanding OBE philosophy, Program and Course Outcomes.'
        },
        {
          name: 'Assessment and Attainment',
          duration: '1 Day',
          level: 'Faculty & HODs',
          description: 'Designing assessments, measuring attainment, and continuous improvement.'
        },
        {
          name: 'Curriculum Design with OBE',
          duration: '1 Day',
          level: 'Curriculum Designers',
          description: 'Aligning curriculum with outcomes and industry requirements.'
        }
      ],
      benefits: [
        'Meet NBA and NAAC outcome requirements',
        'Systematic assessment and evaluation',
        'Better student learning outcomes',
        'Continuous curriculum improvement'
      ]
    },
    {
      id: 'iso-21001',
      title: 'ISO 21001 - Educational Organizations',
      duration: '2-3 Days',
      icon: Shield,
      color: 'text-purple-600',
      borderColor: 'border-purple-600',
      bgColor: 'bg-purple-50',
      description: 'Management system standard for educational organizations.',
      isClickable: true,
      link: '/training/iso-21001-educational-organizations',
      linkText: 'View Full Program',
      courses: [
        {
          name: 'ISO 21001 Awareness',
          duration: '1 Day',
          level: 'All Staff',
          description: 'Understanding ISO 21001 requirements for educational institutions.'
        },
        {
          name: 'ISO 21001 Internal Auditor',
          duration: '2 Days',
          level: 'Quality Team',
          description: 'Conducting internal audits for ISO 21001 compliance.'
        },
        {
          name: 'Implementation Workshop',
          duration: '3 Days',
          level: 'Leadership Team',
          description: 'Implementing ISO 21001 management system.'
        }
      ],
      benefits: [
        'International recognition and credibility',
        'Improved institutional processes',
        'Enhanced stakeholder satisfaction',
        'Competitive advantage'
      ]
    },
    {
      id: 'research-methodology',
      title: 'Research Methodology Training',
      duration: '2-4 Days',
      icon: Lightbulb,
      color: 'text-orange-600',
      borderColor: 'border-orange-600',
      bgColor: 'bg-orange-50',
      description: 'Advanced research methodology and publication skills for faculty.',
      isClickable: true,
      link: '/training/research-methodology',
      linkText: 'View Full Program',
      courses: [
        {
          name: 'Research Design and Methods',
          duration: '2 Days',
          level: 'Faculty & Researchers',
          description: 'Quantitative and qualitative research methodologies.'
        },
        {
          name: 'Academic Writing and Publishing',
          duration: '2 Days',
          level: 'Faculty',
          description: 'Paper writing, journal selection, and publication process.'
        },
        {
          name: 'Grant Writing and Proposal Development',
          duration: '1 Day',
          level: 'Researchers',
          description: 'Writing winning research proposals and securing funding.'
        }
      ],
      benefits: [
        'Increased research output',
        'Quality publications in peer-reviewed journals',
        'Better grant success rate',
        'Enhanced institutional research ranking'
      ]
    },
    {
      id: 'digital-teaching',
      title: 'Digital Teaching Tools and Methods',
      duration: '2-3 Days',
      icon: Brain,
      color: 'text-rose-600',
      borderColor: 'border-rose-600',
      bgColor: 'bg-rose-50',
      description: 'Modern digital tools and technology-enabled teaching methods.',
      isClickable: true,
      link: '/training/digital-teaching-tools',
      linkText: 'View Full Program',
      courses: [
        {
          name: 'Learning Management Systems',
          duration: '1 Day',
          level: 'All Faculty',
          description: 'Using Moodle, Canvas, and other LMS platforms effectively.'
        },
        {
          name: 'E-Content Development',
          duration: '2 Days',
          level: 'All Faculty',
          description: 'Creating engaging digital content, videos, and interactive materials.'
        },
        {
          name: 'AI and EdTech Tools',
          duration: '1 Day',
          level: 'All Faculty',
          description: 'Leveraging AI, chatbots, and modern EdTech for better teaching.'
        }
      ],
      benefits: [
        'Enhanced digital teaching capabilities',
        'Better student engagement online',
        'Modern, interactive content delivery',
        'Improved learning outcomes'
      ]
    }
  ];

  const benefits = [
    {
      title: 'Accreditation Readiness',
      description: 'Meet NBA, NAAC, NIRF, and other accreditation requirements with confidence.',
      icon: Shield,
      color: 'emerald'
    },
    {
      title: 'Quality Enhancement',
      description: 'Improve teaching quality, research output, and institutional processes.',
      icon: TrendingUp,
      color: 'blue'
    },
    {
      title: 'Faculty Development',
      description: 'Enhance faculty skills, confidence, and career growth opportunities.',
      icon: Rocket,
      color: 'purple'
    },
    {
      title: 'Institutional Excellence',
      description: 'Build reputation, rankings, and competitive advantage.',
      icon: Star,
      color: 'orange'
    }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "EducationalOrganization",
    "name": "ISDE Academy",
    "description": pageData.description,
    "url": "https://isde.academy",
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Higher Education Institution Training Programs",
      "itemListElement": trainingPrograms.map(program => ({
        "@type": "Offer",
        "itemOffered": {
          "@type": "Course",
          "name": program.title,
          "description": program.description,
          "provider": {
            "@type": "Organization",
            "name": "ISDE Academy"
          }
        }
      }))
    }
  };

  return (
    <>
      <Helmet>
        <title>{pageData.meta_title}</title>
        <meta name="description" content={pageData.meta_description} />
        <meta name="keywords" content={pageData.meta_keywords} />
        <link rel="canonical" href={pageData.canonicalUrl} />

        <meta property="og:type" content="website" />
        <meta property="og:title" content={pageData.meta_title} />
        <meta property="og:description" content={pageData.meta_description} />
        <meta property="og:url" content={pageData.canonicalUrl} />
        <meta property="og:site_name" content="ISDE Academy" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageData.meta_title} />
        <meta name="twitter:description" content={pageData.meta_description} />

        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-purple-600 via-indigo-600 to-blue-700 text-white py-24 mt-20 overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 -left-4 w-72 h-72 bg-white rounded-full mix-blend-overlay filter blur-xl animate-blob"></div>
            <div className="absolute top-0 -right-4 w-72 h-72 bg-white rounded-full mix-blend-overlay filter blur-xl animate-blob animation-delay-2000"></div>
            <div className="absolute -bottom-8 left-20 w-72 h-72 bg-white rounded-full mix-blend-overlay filter blur-xl animate-blob animation-delay-4000"></div>
          </div>

          <div className="absolute top-10 right-10 opacity-20">
            <Building2 className="h-32 w-32" />
          </div>
          <div className="absolute bottom-10 left-10 opacity-20">
            <GraduationCap className="h-24 w-24" />
          </div>

          <div className="max-w-7xl mx-auto px-4 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center max-w-4xl mx-auto"
            >
              <motion.div
                className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <Award className="h-5 w-5 text-yellow-200" />
                <span className="text-sm font-semibold">Excellence in Higher Education</span>
              </motion.div>

              <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
                {pageData.title}
              </h1>

              <p className="text-xl text-purple-100 mb-8 leading-relaxed max-w-3xl mx-auto">
                {pageData.description}
              </p>

              <div className="flex flex-wrap gap-4 justify-center">
                <Button
                  onClick={() => navigate('/contact')}
                  className="bg-white text-purple-600 hover:bg-purple-50 font-semibold px-8 py-6 text-lg shadow-xl hover:shadow-2xl transition-all"
                >
                  Get Started
                </Button>
                <Button
                  onClick={() => navigate('/training-calendar')}
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-purple-600 font-semibold px-8 py-6 text-lg"
                >
                  View Schedule
                </Button>
              </div>
            </motion.div>
          </div>

          <div className="absolute bottom-0 left-0 right-0">
            <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="#F9FAFB"/>
            </svg>
          </div>
        </section>

        {/* Training Programs Grid */}
        <section className="max-w-7xl mx-auto px-4 py-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-gray-900">Our HEI Training Programs</h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {trainingPrograms.map((program, index) => {
              const IconComponent = program.icon;
              const isClickable = program.isClickable && program.link;

              return (
                <motion.article
                  key={program.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card
                    className={`h-full overflow-hidden hover:shadow-xl transition-all hover:-translate-y-2 group flex flex-col border-t-4 ${program.borderColor} ${isClickable ? 'cursor-pointer' : ''}`}
                    onClick={isClickable ? () => navigate(program.link) : undefined}
                  >
                    <div className={`bg-gradient-to-br ${program.bgColor} bg-opacity-10 p-6`}>
                      <div className={`${program.bgColor} w-16 h-16 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                        <IconComponent className={`h-8 w-8 ${program.color}`} />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-2 min-h-[60px]">
                        {program.title}
                      </h3>
                      <p className={`${program.color} font-semibold text-sm mb-3`}>Duration: {program.duration}</p>
                      <p className="text-gray-600 text-sm leading-relaxed min-h-[60px]">
                        {program.description}
                      </p>
                    </div>

                    <CardContent className="p-6 bg-white flex-1 flex flex-col">
                      <h4 className="font-bold text-sm text-gray-900 mb-3 uppercase tracking-wide">
                        {isClickable ? 'Featured Courses:' : 'Available Courses:'}
                      </h4>
                      <ul className="space-y-3 mb-4 min-h-[180px]">
                        {program.courses.slice(0, 3).map((course, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <BookOpen className={`h-4 w-4 ${program.color} flex-shrink-0 mt-0.5`} />
                            <div className="flex-1">
                              <span className="text-gray-700 text-sm font-medium leading-snug">{course.name}</span>
                              <div className="flex gap-2 mt-1">
                                <span className={`text-xs ${program.color}`}>{course.duration}</span>
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>

                      <div className={`${program.bgColor} p-4 rounded-lg mt-auto`}>
                        <p className={`text-xs font-semibold ${program.color} uppercase tracking-wide mb-1`}>Key Benefit:</p>
                        <p className="text-gray-700 text-sm leading-relaxed min-h-[45px]">{program.benefits[0]}</p>
                      </div>

                      {isClickable && (
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <div className="flex items-center justify-between text-sm font-semibold">
                            <span className={program.color}>{program.linkText || 'View Details'}</span>
                            <ChevronRight className={`h-5 w-5 ${program.color} group-hover:translate-x-1 transition-transform`} />
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </motion.article>
              );
            })}
          </div>
        </section>

        {/* Benefits Section */}
        <section className="bg-white py-16">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-gray-900">
              Why Choose Our HEI Training Programs?
            </h2>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {benefits.map((benefit, index) => {
                const IconComponent = benefit.icon;
                const colors = {
                  emerald: { bg: 'bg-emerald-50', text: 'text-emerald-600' },
                  blue: { bg: 'bg-blue-50', text: 'text-blue-600' },
                  purple: { bg: 'bg-purple-50', text: 'text-purple-600' },
                  orange: { bg: 'bg-orange-50', text: 'text-orange-600' }
                };
                const color = colors[benefit.color];

                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card className="h-full hover:shadow-lg transition-all hover:-translate-y-1">
                      <CardContent className="p-6 text-center">
                        <div className={`${color.bg} w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-4`}>
                          <IconComponent className={`h-6 w-6 ${color.text}`} />
                        </div>
                        <h5 className="text-xl font-bold mb-2 text-gray-900">{benefit.title}</h5>
                        <p className="text-gray-600 text-sm">{benefit.description}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 text-white py-20">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-5xl font-bold mb-6">
                Ready to Transform Your Institution?
              </h2>
              <p className="text-xl text-purple-100 mb-8">
                Join leading educational institutions that have enhanced their quality and reputation with our comprehensive training programs
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={() => navigate('/contact')}
                  className="bg-white text-purple-600 hover:bg-purple-50 font-semibold px-8 py-6 text-lg shadow-xl hover:shadow-2xl transition-all"
                >
                  Request Training
                </Button>
                <Button
                  onClick={() => navigate('/training-calendar')}
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-purple-600 font-semibold px-8 py-6 text-lg"
                >
                  View Training Calendar
                </Button>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default HEITraining;
